export type MergeTagFilterId = "default" | "textCase" | "ratingDisplay";

/** The control rendered for a filter in the chip popover. */
export type MergeTagFilterControl = "text" | "select";

/** A user-supplied filter value, keyed by filter id. */
export type ResolvedFilters = Partial<
  Record<MergeTagFilterId, boolean | string | number>
> & {
  /**
   * DTL filters we don't model (e.g. `floatformat:2`), preserved verbatim and
   * re-emitted after the known chain so authored/pasted tags round-trip.
   */
  passthrough?: readonly string[];
};

export interface MergeTagFilterOption {
  id: string;
  label: string;
}

export const TEXT_CASE_OPTIONS: readonly MergeTagFilterOption[] = [
  { id: "", label: "None" },
  { id: "capfirst", label: "Capitalize first letter" },
  { id: "title", label: "Title Case" },
  { id: "upper", label: "UPPERCASE" },
  { id: "lower", label: "lowercase" },
];

const TEXT_CASE_IDS = new Set(TEXT_CASE_OPTIONS.map((o) => o.id));

/** "" is Number, the plain value, so a tag with no choice renders it. */
export const RATING_DISPLAY_OPTIONS: readonly MergeTagFilterOption[] = [
  { id: "stars", label: "Stars" },
  { id: "", label: "Number" },
];

export interface MergeTagFilterDefinition {
  id: MergeTagFilterId;
  label: string;
  control: MergeTagFilterControl;
  options?: readonly MergeTagFilterOption[];
  /**
   * Where the builder puts the filter. `chain` (the default) is a gonja
   * filter, `else` the if/else fallback, and `display` replaces the `{{ }}`
   * output.
   */
  placement?: "chain" | "else" | "display";
  /**
   * The chain segment, or for `display` the value the builder places. Null
   * when the value is unset or invalid.
   */
  build: (value: boolean | string | number | undefined) => string | null;
}

export const FILTER_REGISTRY: Record<
  MergeTagFilterId,
  MergeTagFilterDefinition
> = {
  default: {
    id: "default",
    label: "Fallback when data is not available",
    control: "text",
    placement: "else",
    build: (value) => {
      if (typeof value !== "string") return null;
      return `default:"${value}"`;
    },
  },
  textCase: {
    id: "textCase",
    label: "Text transform",
    control: "select",
    options: TEXT_CASE_OPTIONS,
    build: (value) => {
      if (
        typeof value !== "string" ||
        value === "" ||
        !TEXT_CASE_IDS.has(value)
      )
        return null;
      // DB values are often title-cased, so bare `capfirst` is a no-op; lower
      // first (matches the product-grid name transform).
      return value === "capfirst" ? "lower|capfirst" : value;
    },
  },
  ratingDisplay: {
    id: "ratingDisplay",
    label: "Show as",
    control: "select",
    options: RATING_DISPLAY_OPTIONS,
    placement: "display",
    build: (value) => (value === "stars" ? value : null),
  },
};

export const FILTER_ORDER: readonly MergeTagFilterId[] = [
  "default",
  "textCase",
  "ratingDisplay",
];

/** All filter definitions, in emission order. */
export function list(): MergeTagFilterDefinition[] {
  return FILTER_ORDER.map((id) => FILTER_REGISTRY[id]);
}

export function normalize(raw: ResolvedFilters | undefined): ResolvedFilters {
  if (!raw) return {};
  const legacy = raw as Record<string, unknown>;
  if (legacy.capfirst === undefined && legacy.truncatechars === undefined) {
    return raw;
  }

  const migrated: ResolvedFilters = { ...raw };
  delete (migrated as Record<string, unknown>).capfirst;
  delete (migrated as Record<string, unknown>).truncatechars;
  if (legacy.capfirst === true && migrated.textCase === undefined) {
    migrated.textCase = "capfirst";
  }
  return migrated;
}

export function resolve(raw: ResolvedFilters | undefined): ResolvedFilters {
  const normalized = normalize(raw);
  const resolved: ResolvedFilters = {};
  for (const id of FILTER_ORDER) {
    const value = normalized[id];
    if (value === undefined) continue;
    if (FILTER_REGISTRY[id].build(value) !== null) {
      resolved[id] = value;
    }
  }
  if (
    normalized.passthrough !== undefined &&
    normalized.passthrough.length > 0
  ) {
    resolved.passthrough = normalized.passthrough;
  }
  return resolved;
}

export const FALLBACK_VALUE_MAX_LENGTH = 60;
const FALLBACK_INVALID_CHAR_PATTERN = /[{}"\\]/g;

/** Strips DTL-breaking chars and caps length for a `default:"..."` fallback value. */
export function sanitizeFallback(input: string): string {
  return input
    .replace(FALLBACK_INVALID_CHAR_PATTERN, "")
    .slice(0, FALLBACK_VALUE_MAX_LENGTH);
}

/**
 * The canvas text for a field that renders only above 0 (see `build.ts`):
 * stars or the number. Empty when the value is not above 0, as the send
 * renders nothing. `Math.floor` matches gonja's `|int`, which truncates
 * positive values.
 */
export function previewPositiveOnly(
  value: string,
  filters: ResolvedFilters,
): string {
  const number = Number.parseFloat(value);
  if (!(number > 0)) return "";
  return FILTER_REGISTRY.ratingDisplay.build(filters.ratingDisplay) === "stars"
    ? "★".repeat(Math.floor(number))
    : value;
}

export function previewTextCase(text: string, id: unknown): string {
  switch (id) {
    case "capfirst":
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    case "upper":
      return text.toUpperCase();
    case "lower":
      return text.toLowerCase();
    case "title":
      return text.replace(
        /\S+/g,
        (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
      );
    default:
      return text;
  }
}
